import { AppBar } from "@/elements/components/ui/app-bar";
import { Button } from "@/elements/components/ui/button";
import { Input } from "@/elements/components/ui/input";
import { Redirect, useLocalSearchParams, useRouter } from "expo-router";
import React, { useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuthActions } from "@convex-dev/auth/react";
import { handleAuthError } from "@/elements/components/utils/auth-error-handler";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { auth } from "@/elements/components/utils/convex-api";
import Loader from "@/elements/components/shared/loader";
import ResponsiveContent from "@/elements/components/shared/responsive-content";
import Toast from "react-native-toast-message";

const ForgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordSchemaType = z.infer<typeof ForgotPasswordSchema>;

function ForgotPasswordScreen() {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      email: "",
    },
    mode: "onChange",
    resolver: zodResolver(ForgotPasswordSchema),
  });
  const { signIn } = useAuthActions();
  const { persona } = useLocalSearchParams<{
    persona?: "parent" | "tutor" | "student";
  }>();

  const { data: isAuthenticated = false, isSuccess } = useQuery(
    convexQuery(auth.isAuthenticated, {})
  );

  if (!persona) {
    return <Redirect href={"/(auth)/sign-in-options"} />;
  }

  if (!isSuccess) {
    return <Loader />;
  }

  if (isAuthenticated) {
    return <Redirect href={"/(app)/home"} />;
  }

  const onSubmit = async (data: ForgotPasswordSchemaType) => {
    try {
      setIsSubmitting(true);
      await signIn("password", {
        email: data.email.toLowerCase().trim(),
        flow: "reset",
      });
      router.push({
        pathname: "/(auth)/verify-reset-code",
        params: { email: data.email.toLowerCase().trim(), persona },
      });
    } catch (error) {
      const errorResult = handleAuthError(error);
      console.log(errorResult);
      Toast.show({
        type: "error",
        text1: errorResult.message,
        text1Style: {
          fontFamily: "Geologica_400Regular",
          fontSize: 10,
          color: "#000000",
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <View className="flex-1 bg-teachLoop-creamLight">
      <AppBar>
        <AppBar.Icon
          onPress={() =>
            router.canGoBack()
              ? router.back()
              : router.replace({
                  pathname: "/(auth)/sign-in",
                  params: { persona },
                })
          }
        />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView showsVerticalScrollIndicator={false}>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-4" />
            <View className="gap-1">
              <Text className="font-zilla-slab-medium leading-[1.2] font-medium text-[28px] text-black">
                Enter your email
              </Text>
              <Text className="font-geologica-regular font-normal text-[20px] text-black leading-[1.5] ">
                Start with sharing the email associated with this account
              </Text>
            </View>
            <View className="h-8" />
            <View className="gap-4">
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <Input
                    size="md"
                    placeholder="Email"
                    {...field}
                    error={!!fieldState.error?.message}
                    keyboardType="email-address"
                    autoCapitalize="none"
                  />
                )}
              />
            </View>
            <View className="h-8" />
            <Button
              className="w-full"
              size="md"
              intent="primary"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              <Button.Text size="md">Next</Button.Text>
            </Button>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </View>
  );
}

export default ForgotPasswordScreen;
